/**
 * Browser-safe Tier 2 bundled-genome registry.
 *
 * The registry is an in-memory Map<pathogenId, BundledGenome>. It is
 * populated either via the Node side-effect loader (loader-node.ts —
 * scans data/genomes/*.fasta) or via __setBundledGenomes from a
 * browser app that bundles the FASTAs at build time with Vite's
 * import.meta.glob.
 *
 * No fs imports here so this module is bundleable for the browser.
 */

export interface BundledGenome {
  pathogenId: string;
  /** Raw FASTA text (header + sequence lines). */
  fasta: string;
  /** Single-line sequence (header stripped, whitespace removed, uppercased). */
  sequence: string;
  /** Header line (without leading '>'). */
  header: string;
  /** Accession the FASTA was fetched against. */
  accession: string;
  /** Sequence length in bp. */
  sequenceLengthBp: number;
  /** ISO date the FASTA was fetched. */
  fetchedAt: string;
}

const REGISTRY = new Map<string, BundledGenome>();

/** Replace the bundled-genome registry. Used by both Node fs loader and
 *  browser apps injecting FASTAs via import.meta.glob. */
export function __setBundledGenomes(genomes: BundledGenome[]): void {
  REGISTRY.clear();
  for (const g of genomes) REGISTRY.set(g.pathogenId, g);
}

/** Parse a raw FASTA + metadata JSON into a BundledGenome record. */
export function parseBundledGenome(
  pathogenId: string, fasta: string, meta: { accession: string; sequenceLengthBp: number; fetchedAt: string },
): BundledGenome {
  const lines = fasta.split(/\r?\n/);
  let header = '';
  let seq = '';
  for (const line of lines) {
    if (line.startsWith('>')) header = line.slice(1).trim();
    else seq += line.trim().toUpperCase();
  }
  return {
    pathogenId, fasta, sequence: seq, header,
    accession: meta.accession,
    sequenceLengthBp: meta.sequenceLengthBp,
    fetchedAt: meta.fetchedAt,
  };
}

export function loadBundledGenome(pathogenId: string): BundledGenome {
  const g = REGISTRY.get(pathogenId);
  if (!g) throw new Error(`Tier 2 reference genome not bundled for '${pathogenId}'. Populate via Node loader-node or browser __setBundledGenomes.`);
  return g;
}

export function hasBundledGenome(pathogenId: string): boolean {
  return REGISTRY.has(pathogenId);
}
